import React, {ReactNode} from "react";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import {CanvasLayer, PointTooltip, ResponsiveLineCanvas, Serie} from "@nivo/line";
import Translate from "../../../shared/translation/Translate";
import {OrdinalColorScaleConfig} from "@nivo/colors";
import {ScaleSpec} from "@nivo/scales";
import {LegendProps} from "@nivo/legends";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

interface LineChartProperties extends I18nGlobals {
	data: Array<Serie>;
	label?: string;
	legendX?: string;
	legendY?: string;
	tickValues?: string;
	timeFormat?: string;
	enablePoints?: boolean;
	enableArea?: boolean;
	curve?: "linear" | "stepAfter";
	tooltip?: PointTooltip;
	layers?: Array<CanvasLayer>;
	colors?: OrdinalColorScaleConfig;
	yScale?: ScaleSpec;
	legends?: Array<LegendProps>;
}

interface LineChartState {
}

export class LineChartDefinition extends PortalPureComponent<LineChartProperties, LineChartState> {
	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<div className="p-col-12">
				{FunctionHelper.exists(this.props.label) &&
					<h2>{this.props.label}</h2>
				}
				{this.props.data.length === 0 ?
					<p className="no-results">
						<Translate {...this.props} data={"adminscreen.panels.statistics.chart.empty"}
								   namespace={"screen"}/>
					</p> :
					<div style={{height: "500px", width: "100%"}}>
						<ResponsiveLineCanvas data={this.props.data}
											  layers={this.props.layers}
											  enableArea={FunctionHelper.applyValue(
												  this.props.enableArea, false
											  )}
											  curve={FunctionHelper.applyValue(
												  this.props.curve, "linear"
											  )}
											  tooltip={this.props.tooltip}
											  margin={{top: 60, right: 20, bottom: 60, left: 60}}
											  xScale={{
												  type: "time",
												  format: "%Y-%m-%dT%H:%M:%S.%L%Z"
											  }}
											  yScale={FunctionHelper.applyValue(this.props.yScale, {
												  type: "linear",
												  min: 0,
												  max: "auto"
											  })}
											  axisBottom={{
												  tickValues: this.props.tickValues,
												  format: this.props.timeFormat,
												  legend: this.props.legendX,
												  legendOffset: 36,
												  legendPosition: "middle"
											  }}
											  axisLeft={{
												  legend: this.props.legendY,
												  legendOffset: -40,
												  legendPosition: "middle"
											  }}
											  colors={FunctionHelper.applyValue(
												  this.props.colors, {scheme: "nivo"}
											  )}
											  pointSize={10}
											  pointBorderWidth={2}
											  lineWidth={this.props.enableArea ? 0 : 2}
											  enablePoints={this.props.enablePoints}
											  areaOpacity={0.5}
											  enableSlices="x"
											  legends={FunctionHelper.applyValue(this.props.legends, [
												  {
													  anchor: "top",
													  direction: "row",
													  justify: false,
													  translateX: 0,
													  translateY: -40,
													  itemsSpacing: 0,
													  itemDirection: "left-to-right",
													  itemWidth: 90,
													  itemHeight: 20,
													  itemOpacity: 0.75,
													  symbolSize: 10,
													  symbolShape: "circle"
												  }
											  ])}/>
					</div>
				}
			</div>
		);
	}
}

const LineChart = React.memo(LineChartDefinition,
	(prevProps: Readonly<LineChartProperties>, nextProps: Readonly<LineChartProperties>) => {
		if (prevProps.data !== nextProps.data) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (prevProps.legendX !== nextProps.legendX) {
			return false;
		}

		if (prevProps.legendY !== nextProps.legendY) {
			return false;
		}

		if (prevProps.tickValues !== nextProps.tickValues) {
			return false;
		}

		if (prevProps.timeFormat !== nextProps.timeFormat) {
			return false;
		}

		return true;
	}
);

export default LineChart;